import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  BookOpen, Calendar, Eye, User, Tag, Bookmark, BookmarkCheck,
  Share2, ArrowLeft, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import LoadingSpinner from '../components/common/LoadingSpinner';
import AlertBanner from '../components/common/AlertBanner';
import ReactMarkdown from 'react-markdown';

const KnowledgeArticleDetailPage = () => {
  const navigate = useNavigate();
  const { articleId } = useParams();
  const [loading, setLoading] = useState(true);
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchArticleDetail();
  }, [articleId]);

  const fetchArticleDetail = async () => {
    try {
      const token = localStorage.getItem('xchange_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;

      const res = await fetch(`${apiUrl}/api/knowledge/articles/${articleId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setArticle(data.article);
        setRelatedArticles(data.related || []);
        
        // Check if bookmarked
        const bookmarksRes = await fetch(`${apiUrl}/api/knowledge/bookmarks`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (bookmarksRes.ok) {
          const bookmarks = await bookmarksRes.json();
          setIsBookmarked(bookmarks.some(b => b.articleId === articleId));
        }
      } else {
        setError('Article not found');
      }
    } catch (error) {
      console.error('Error fetching article:', error);
      setError('Failed to load article');
    } finally {
      setLoading(false);
    }
  };

  const handleBookmark = async () => {
    try {
      const token = localStorage.getItem('xchange_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;

      if (isBookmarked) {
        const res = await fetch(`${apiUrl}/api/knowledge/bookmarks/${articleId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          setIsBookmarked(false);
        }
      } else {
        const res = await fetch(`${apiUrl}/api/knowledge/articles/${articleId}/bookmark`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          setIsBookmarked(true);
        }
      }
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: article.title,
        text: article.summary,
        url: window.location.href
      });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'law_update': return 'bg-blue-100 text-blue-800';
      case 'template': return 'bg-green-100 text-green-800';
      case 'case_study': return 'bg-purple-100 text-purple-800';
      case 'training': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getReadingTime = (content) => {
    const wordsPerMinute = 200;
    const wordCount = content.split(/\s+/).length;
    const minutes = Math.ceil(wordCount / wordsPerMinute);
    return `${minutes} min read`;
  };

  if (loading) {
    return <LoadingSpinner fullScreen size="xl" />;
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <AlertBanner type="error" message={error || 'Article not found'} className="mb-6" />
          <Button onClick={() => navigate('/knowledge')} variant="outline">
            ← Back to Knowledge Hub
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/knowledge')}
          className="flex items-center text-teal-600 hover:text-teal-700 mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Knowledge Hub
        </button>

        {/* Article Header */}
        <div className="bg-white rounded-lg shadow p-8 mb-6">
          {/* Category Badge */}
          <div className="flex items-center gap-3 mb-4">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(article.category)}`}>
              {article.category.replace('_', ' ').toUpperCase()}
            </span>
            {article.featured && (
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                ⭐ Featured
              </span>
            )}
          </div>

          {/* Title */}
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {article.title}
          </h1>

          {/* Summary */}
          <p className="text-lg text-gray-600 mb-6">
            {article.summary}
          </p>

          {/* Meta Information */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-6 pb-6 border-b">
            <div className="flex items-center">
              <User className="w-4 h-4 mr-2" />
              {article.author}
            </div>
            <div className="flex items-center">
              <Calendar className="w-4 h-4 mr-2" />
              {new Date(article.publishedDate).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </div>
            <div className="flex items-center">
              <Eye className="w-4 h-4 mr-2" />
              {article.views} views
            </div>
            <div className="flex items-center">
              <Clock className="w-4 h-4 mr-2" />
              {getReadingTime(article.content)}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              onClick={handleBookmark}
              variant="outline"
              className={isBookmarked ? 'text-teal-600 border-teal-600' : 'text-gray-600'}
            >
              {isBookmarked ? (
                <>
                  <BookmarkCheck className="w-4 h-4 mr-2 fill-current" />
                  Bookmarked
                </>
              ) : (
                <>
                  <Bookmark className="w-4 h-4 mr-2" />
                  Bookmark
                </>
              )}
            </Button>
            <Button
              onClick={handleShare}
              variant="outline"
              className="text-gray-600"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
          </div>
        </div>

        {/* Article Content */}
        <div className="bg-white rounded-lg shadow p-8 mb-6">
          <div className="prose prose-teal prose-lg max-w-none">
            <ReactMarkdown>{article.content}</ReactMarkdown>
          </div>
        </div>

        {/* Tags */}
        {article.tags && article.tags.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Tag className="w-5 h-5 mr-2 text-teal-600" />
              Tags
            </h3>
            <div className="flex flex-wrap gap-2">
              {article.tags.map(tag => (
                <button
                  key={tag}
                  onClick={() => navigate(`/knowledge?tag=${tag}`)}
                  className="px-3 py-1 bg-gray-100 hover:bg-teal-50 text-gray-700 hover:text-teal-700 text-sm rounded-full transition-colors"
                >
                  #{tag}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <BookOpen className="w-5 h-5 mr-2 text-teal-600" />
              Related Articles
            </h3>
            <div className="space-y-4">
              {relatedArticles.map(related => (
                <div
                  key={related.id}
                  onClick={() => {
                    navigate(`/knowledge/article/${related.id}`);
                    window.scrollTo(0, 0);
                  }}
                  className="p-4 border border-gray-200 rounded-lg hover:border-teal-500 hover:shadow-md transition-all cursor-pointer"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getCategoryColor(related.category)}`}>
                        {related.category.replace('_', ' ').toUpperCase()}
                      </span>
                      <h4 className="text-base font-semibold text-gray-900 mt-2 hover:text-teal-600">
                        {related.title}
                      </h4>
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                        {related.summary}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KnowledgeArticleDetailPage;
